/**
 * SOLID Review
 * S — Strong. One concern: declare the sockets ComponentFrontendApplication requires. No state, no
 *     lifecycle, no `connectAll`.
 * O — Strong. A new dependency is one more field here and one more connector in `FrontendConnectors`.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Every field is a `RequiredPortWithDefault<T>` over the contract's derived interface.
 * D — Strong. The kernel, the Domain's contract and the Application's own contracts.
 */
import { PortHolder } from '@ml3d/kernel';
import { RequiredPortWithDefault } from '@ml3d/kernel/required-with-default';
import { ClientDecisionsContract, type IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import {
  ClientStateContract, ClientRoundServerContract, ClientRoomServerContract, ClientServerWatchContract,
  ClientTabStorageContract, ClientPlayerServerContract, ClientAuthServerContract, ClientLegalServerContract,
  ClientConsentPlatformContract, ClientPaymentServerContract, ClientAdsServerContract, ClientServerHealthContract,
  ClientMediaServerContract,
  type IPortClientState, type IPortClientRoundServer, type IPortClientRoomServer, type IPortClientServerWatch,
  type IPortClientTabStorage, type IPortClientPlayerServer, type IPortClientAuthServer, type IPortClientLegalServer,
  type IPortClientConsentPlatform, type IPortClientPaymentServer, type IPortClientAdsServer, type IPortClientServerHealth,
  type IPortClientMediaServer,
} from '../ports/contracts';
import { unavailable } from '../ports/unavailable';

/**
 * Every socket ComponentFrontendApplication requires (frontend strict-ports plan, FE1, 2026-10-03) —
 * the twin of the backend's `ComponentApplicationRequiredPorts`.
 *
 * CONNECTED FROM OUTSIDE, by `FrontendConnectors` (FE1d), one phase after construction and one phase
 * before `build()` reads them. These are sockets `assertAssembled` can genuinely observe unconnected.
 *
 * `RequiredPortWithDefault`, never the throwing variant (`frontend-no-throwing-port`): an unconnected
 * socket answers `UNAVAILABLE` through its contract, so a missing adapter degrades a move instead of
 * white-screening the app. The production-wiring test is the gate that keeps one from shipping.
 *
 * PUBLIC AND READONLY: `describePorts()` walks own enumerable fields, and descends into this holder
 * because it extends `PortHolder`; drop the `extends` and these ports vanish from every diagram and
 * assertion without anything throwing. A test pins that a walk of the component equals the sum of its
 * holders. The basename starts with `Component` and every constructor carries its `<T>`, or
 * `lint:explained-uml` stops seeing the names (§28.105).
 */
export class ComponentFrontendApplicationRequiredPorts extends PortHolder {
  /** The Domain's decisions — the only place the table's rules live. */
  readonly clientDecisionsSocket = new RequiredPortWithDefault<IPortClientDecisions>(
    'ClientDecisions', unavailable<IPortClientDecisions>(ClientDecisionsContract), ClientDecisionsContract);
  /** The table and the session as the stores hold them: read before a decision, written after. */
  readonly clientStateSocket = new RequiredPortWithDefault<IPortClientState>(
    'ClientState', unavailable<IPortClientState>(ClientStateContract), ClientStateContract);
  /** The round on the server: start, roll, peek, claim, believe, lift, and the two active-round reads. */
  readonly clientRoundServerSocket = new RequiredPortWithDefault<IPortClientRoundServer>(
    'ClientRoundServer', unavailable<IPortClientRoundServer>(ClientRoundServerContract), ClientRoundServerContract);
  /** The room on the server: its roster, its code lookup, leaving it, and taking a seat (FE2). */
  readonly clientRoomServerSocket = new RequiredPortWithDefault<IPortClientRoomServer>(
    'ClientRoomServer', unavailable<IPortClientRoomServer>(ClientRoomServerContract), ClientRoomServerContract);
  /** Which of the server's streams reach `ClientServerEvents`. */
  readonly clientServerWatchSocket = new RequiredPortWithDefault<IPortClientServerWatch>(
    'ClientServerWatch', unavailable<IPortClientServerWatch>(ClientServerWatchContract), ClientServerWatchContract);
  /** What the tab keeps across a reload: the stored game and the guest identity (FE2). */
  readonly clientTabStorageSocket = new RequiredPortWithDefault<IPortClientTabStorage>(
    'ClientTabStorage', unavailable<IPortClientTabStorage>(ClientTabStorageContract), ClientTabStorageContract);
  /** Players on the server: a guest, created by name (FE2). */
  readonly clientPlayerServerSocket = new RequiredPortWithDefault<IPortClientPlayerServer>(
    'ClientPlayerServer', unavailable<IPortClientPlayerServer>(ClientPlayerServerContract), ClientPlayerServerContract);
  /** The signed-in account — `me` and signing out; the token never crosses (FE2, ahead of FE3). */
  readonly clientAuthServerSocket = new RequiredPortWithDefault<IPortClientAuthServer>(
    'ClientAuthServer', unavailable<IPortClientAuthServer>(ClientAuthServerContract), ClientAuthServerContract);
  /** The legal documents and the account's legal acts (FE4a). */
  readonly clientLegalServerSocket = new RequiredPortWithDefault<IPortClientLegalServer>(
    'ClientLegalServer', unavailable<IPortClientLegalServer>(ClientLegalServerContract), ClientLegalServerContract);
  /** The consent management platform, in the browser (FE4b). */
  readonly clientConsentPlatformSocket = new RequiredPortWithDefault<IPortClientConsentPlatform>(
    'ClientConsentPlatform', unavailable<IPortClientConsentPlatform>(ClientConsentPlatformContract), ClientConsentPlatformContract);
  /** The account's checkout and balance (FE4c). */
  readonly clientPaymentServerSocket = new RequiredPortWithDefault<IPortClientPaymentServer>(
    'ClientPaymentServer', unavailable<IPortClientPaymentServer>(ClientPaymentServerContract), ClientPaymentServerContract);
  /** The ad provider the server chose for a player (FE4d). */
  readonly clientAdsServerSocket = new RequiredPortWithDefault<IPortClientAdsServer>(
    'ClientAdsServer', unavailable<IPortClientAdsServer>(ClientAdsServerContract), ClientAdsServerContract);
  /** The server's /health (FE5a). */
  readonly clientServerHealthSocket = new RequiredPortWithDefault<IPortClientServerHealth>(
    'ClientServerHealth', unavailable<IPortClientServerHealth>(ClientServerHealthContract), ClientServerHealthContract);
  /** The video chat's signaling and admin reads (FE5b). */
  readonly clientMediaServerSocket = new RequiredPortWithDefault<IPortClientMediaServer>(
    'ClientMediaServer', unavailable<IPortClientMediaServer>(ClientMediaServerContract), ClientMediaServerContract);
}
